import type { Component } from "solid-js";
import { createMemo, createSignal, For, Show } from "solid-js";
import type { IconProps } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import SearchBar from "../searchBar/SearchBar";
import { Popover, PopoverContext } from "@ark-ui/solid";
import { useTranslation } from "../../state/i18n";
import { softContains } from "../../utils/stringUtil";
import type { SelectableIconName } from "../../utils/iconUtil";
import { isNil } from "lodash-es";
import type { ThemeName } from "../../types";
import { themeIconColour } from "../../types";

type IconSelectProps = {
  size: "sm" | "md" | "lg";
  theme?: ThemeName;
  disabled: boolean;
  errorMessage?: string;
} & IconPickerProps;

const IconSelect: Component<IconSelectProps> = (props) => {
  const { t } = useTranslation();
  const hasError = () => props.errorMessage !== undefined;
  const iconSize = createMemo<IconProps["size"]>(() => {
    switch (props.size) {
      case "sm":
        return "lg";
      case "md":
        return "2xl";
      case "lg":
        return "3xl";
    }
  });

  return (
    <Popover.Root
      modal
      positioning={{
        placement: "bottom-start",
        gutter: 4,
      }}
    >
      <Popover.Trigger
        disabled={props.disabled}
        asChild={(triggerProps) => (
          <button
            {...triggerProps}
            aria-label={t(($) => $.ICON_SELECT_ARIA_LABEL)}
            class="rounded-sm border focus-visible:outline-default"
            classList={{
              "size-9.5 p-2": props.size === "sm",
              "size-15 p-3": props.size === "md",
              "size-21 p-4": props.size === "lg",
              "cursor-pointer hover:bg-surface-extra-subtle-hover": !props.disabled,
              "border-default bg-surface-extra-subtle": !hasError(),
              "border-error bg-surface-destructive": hasError(),
            }}
          >
            <Icon
              name={props.value}
              type="solid"
              size={iconSize()}
              class={isNil(props.theme) ? "icon-tertiary" : themeIconColour[props.theme]}
            />
          </button>
        )}
      />

      <PopoverContext>
        {(context) => (
          <Show when={context().open}>
            <Popover.Positioner>
              <Popover.Content class="z-10">
                <IconPicker icons={props.icons} value={props.value} onChange={props.onChange} />
              </Popover.Content>
            </Popover.Positioner>
          </Show>
        )}
      </PopoverContext>
    </Popover.Root>
  );
};

type IconPickerProps = {
  icons: SelectableIconName[];
  value: SelectableIconName;
  onChange: (value: SelectableIconName) => void;
};

const IconPicker: Component<IconPickerProps> = (props) => {
  const { t } = useTranslation();
  const [query, setQuery] = createSignal("");
  const filteredIcons = createMemo(() => props.icons.filter((icon) => softContains(icon, query())));

  return (
    <Popover.Context>
      {(context) => (
        <div class="flex size-60 flex-col overflow-hidden rounded-sm bg-surface-default pt-4 shadow-elevation-light-3">
          <div class="px-2.5">
            <SearchBar
              placeholder={t(($) => $.ICON_SELECT_SEARCH_PLACEHOLDER)}
              query={query()}
              onQueryChange={setQuery}
            />
          </div>

          <div class="mt-2 flex flex-wrap gap-1 overflow-y-auto px-2.5 pb-2">
            <For each={filteredIcons()} fallback={<NoResults />}>
              {(icon) => {
                const isSelected = icon === props.value;

                return (
                  <button
                    type="button"
                    aria-label={icon}
                    aria-pressed={isSelected}
                    onClick={() => {
                      context().setOpen(false);
                      props.onChange(icon);
                    }}
                    class="rounded-sm p-1 -outline-offset-1 hover:bg-button-bg-tertiary-hover focus-visible:outline-default"
                    classList={{
                      "bg-button-bg-tertiary-active [&>svg]:icon-button-icon-tertiary-active": isSelected,
                      "bg-button-bg-tertiary-default [&>svg]:icon-button-icon-tertiary-soft": !isSelected,
                    }}
                  >
                    <Icon name={icon} type="solid" size="md" />
                  </button>
                );
              }}
            </For>
          </div>
        </div>
      )}
    </Popover.Context>
  );
};

const NoResults = () => {
  const { t } = useTranslation();
  return <div class="py-4 text-medium-regular text-secondary">{t(($) => $.ICON_SELECT_SEARCH_NO_RESULTS)}</div>;
};

export default IconSelect;
